import { Money } from '@chukua/common';
import { Alert, Button, Drawer, Form } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { EditProposalFields } from './EditProposalFields';
import { editProposal, type RateLineProposalView } from '../../../rate-cards.api';
import { failureMessageFor } from '../../../policies/rate-line-failure-message';
import { formatLineKey } from '../../../policies/rate-line-key';

interface EditFormValues {
  amount: number;
  currency: RateLineProposalView['amount']['currency'];
  emptyReturnIncluded?: boolean;
  validFrom: Dayjs;
  validTo?: Dayjs | null;
  reason: string;
}

interface EditProposalDrawerProps {
  proposal: RateLineProposalView | null;
  onSaved: () => void;
  onClose: () => void;
}

/** A draft's author adjusts price, flag or dates before submitting; the key and kind never change. */
export function EditProposalDrawer({ proposal, onSaved, onClose }: EditProposalDrawerProps) {
  const [form] = Form.useForm<EditFormValues>();
  const { error, submitting, submit, clearError } = useSubmission();
  const toast = useToast();

  useEffect(() => {
    if (!proposal) return;
    form.setFieldsValue({
      amount: Number(Money.fromJSON(proposal.amount).toDecimalString()),
      currency: proposal.amount.currency,
      emptyReturnIncluded: proposal.emptyReturnIncluded ?? undefined,
      validFrom: dayjs(proposal.validFrom),
      validTo: proposal.validTo ? dayjs(proposal.validTo) : undefined,
      reason: '',
    });
  }, [proposal, form]);

  async function save(values: EditFormValues) {
    if (!proposal) return;
    const saved = await submit(
      () =>
        editProposal(proposal.id, {
          amount: Money.parse(String(values.amount), values.currency).toJSON(),
          emptyReturnIncluded: values.emptyReturnIncluded,
          validFrom: values.validFrom.format('YYYY-MM-DD'),
          validTo: values.validTo?.format('YYYY-MM-DD'),
          reason: values.reason,
        }).then(() => true),
      failureMessageFor,
    );
    if (!saved) return;
    toast.success('Draft updated');
    onSaved();
    onClose();
  }

  return (
    <Drawer open={proposal !== null} onClose={onClose} afterOpenChange={clearError} title="Edit draft" placement="right" size={480} destroyOnClose>
      {proposal && <p className="mb-5 text-sm text-muted">{formatLineKey(proposal)}</p>}
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<EditFormValues> form={form} layout="vertical" requiredMark={false} onFinish={save} disabled={submitting}>
        <EditProposalFields proposal={proposal} />
        <Button type="primary" htmlType="submit" loading={submitting}>
          Save draft
        </Button>
      </Form>
    </Drawer>
  );
}
